import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

// The sign-in tablet's offline support. While online it keeps a names-only copy of today's roll; while offline the family-facing screen becomes an "offline sheet" built from that copy,
// and every sign-in/out is kept here (with its real time, name and signature) until the connection returns and it can be sent. Nothing but names and sessions is stored: no phone
// numbers, no medical or contact details.

const QUEUE_KEY = "oh_kiosk_queue_v1";
const ROLL_KEY = "oh_kiosk_roll_v1";
const CONFIG_KEY = "oh_kiosk_config_v1";

const read = (k, fallback) => { try { const v = JSON.parse(localStorage.getItem(k) || "null"); return v ?? fallback; } catch { return fallback; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch { return false; } };

export const loadQueue = () => read(QUEUE_KEY, []);
export const loadRoll = () => read(ROLL_KEY, null);
export const loadConfig = () => read(CONFIG_KEY, null);
export const saveConfig = (c) => write(CONFIG_KEY, c);
export function clearOffline() { try { localStorage.removeItem(ROLL_KEY); localStorage.removeItem(CONFIG_KEY); } catch { /* storage blocked */ } }   // the queue is deliberately kept: it is a record that still has to reach the server

export const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

/** Keep a sign-in/out for sending later, and reflect it in the local copy of the roll so the sheet shows the child as in (or out) straight away. Returns false if the tablet couldn't store it. */
export function recordOffline(entry) {
  const q = loadQueue();
  q.push(entry);
  if (!write(QUEUE_KEY, q)) return false;
  const roll = loadRoll();
  if (roll) { write(ROLL_KEY, { ...roll, children: roll.children.map((c) => (c.bookingId === entry.bookingId ? { ...c, state: entry.action === "checkin" ? "in" : "out", offlineAt: entry.at } : c)) }); }
  return true;
}

export async function fetchRoll(api) {
  try {
    const r = await axios.get(`${api}/kiosk/offline-roll`, { silent: true, timeout: 8000 });
    if (r.data?.enabled) write(ROLL_KEY, r.data); else { try { localStorage.removeItem(ROLL_KEY); } catch { /* storage blocked */ } }
    return r.data;
  } catch { return null; }
}

let syncing = null;
/** Send what was kept. Entries the server answered for (recorded, already there, or a problem it has logged for staff) are dropped; anything else (no connection, signed out, server trouble) stays for next time. */
export function syncQueue(api) {
  if (syncing) return syncing;
  const q = loadQueue();
  if (!q.length) return Promise.resolve({ sent: 0, remaining: 0, problems: 0 });
  syncing = (async () => {
    let sent = 0; let problems = 0;
    try {
      for (let i = 0; i < q.length; i += 50) {
        const batch = q.slice(i, i + 50);
        const r = await axios.post(`${api}/kiosk/offline-sync`, { entries: batch }, { timeout: 20000 });
        const done = new Set((r.data?.results || []).map((x) => x.id));
        problems += (r.data?.results || []).filter((x) => x.result === "problem").length;
        sent += done.size;
        write(QUEUE_KEY, loadQueue().filter((e) => !done.has(e.id)));
      }
    } catch (e) {
      if (e.response?.status === 403) { /* switched off for this programme: keep them, staff can see the tablet and decide */ }
    }
    return { sent, remaining: loadQueue().length, problems };
  })().finally(() => { syncing = null; });
  return syncing;
}

/** Is the server reachable? A tiny ping every few seconds; two misses in a row mean offline, one answer (of any kind) means online. Also keeps the local roll fresh and sends the queue when the connection is back. */
export function useKioskConnection(api, active, onBack) {
  const [online, setOnline] = useState(true);
  const [queued, setQueued] = useState(() => loadQueue().length);
  const fails = useRef(0);
  const was = useRef(true);
  const lastRoll = useRef(0);
  const backCb = useRef(onBack);
  backCb.current = onBack;

  const tick = useCallback(async () => {
    let ok = true;
    try { await axios.get(`${api}/kiosk/ping`, { silent: true, timeout: 4000 }); } catch (e) { ok = !!e.response; }
    fails.current = ok ? 0 : fails.current + 1;
    const nowOnline = fails.current < 2;
    setOnline(nowOnline);
    if (nowOnline) {
      const cameBack = !was.current;
      if (cameBack && backCb.current) backCb.current();
      if (loadQueue().length) { await syncQueue(api); }
      if (cameBack || Date.now() - lastRoll.current > 2 * 60 * 1000) { lastRoll.current = Date.now(); await fetchRoll(api); }
    }
    was.current = nowOnline;
    setQueued(loadQueue().length);
    return ok;
  }, [api]);

  useEffect(() => {
    if (!active || !api) return undefined;
    let stop = false; let t;
    const loop = async () => { await tick(); if (!stop) t = setTimeout(loop, fails.current > 0 ? 2500 : 8000); };
    loop();
    const kick = () => { clearTimeout(t); loop(); };
    window.addEventListener("online", kick); window.addEventListener("offline", kick);
    return () => { stop = true; clearTimeout(t); window.removeEventListener("online", kick); window.removeEventListener("offline", kick); };
  }, [api, active, tick]);

  const refreshQueued = useCallback(() => setQueued(loadQueue().length), []);
  return { online, queued, refreshQueued };
}
